import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Ban } from 'lucide-react';

import GenericFormDrawer from '../../components/GenericFormDrawer';
import CheckboxInput from '../../components/ui/CheckboxInput';

// Çalışma saati seçici — vardiya çizelgesi hücresi (ShiftSchedulesPage) ve rotasyon günleri
// (ShiftRotationDetailsPage) için. Eskiden ortada açılan bir modaldı (WorkHourPickerModal); UI altın
// kuralı gereği sağdan açılan çekmeceye taşındı: GenericFormDrawer KABUK deseni (schema=[] + children).
// Etkileşim "seç → Kaydet": listeden bir çalışma saati ya da "Vardiyasız Bırak" seçilir (vurgulanır),
// işaretler istenirse değiştirilir, çekmecenin Kaydet düğmesi seçimi `onSelect` ile bildirir.
//
// SÖZLEŞME (çağıranlar değişmedi): onSelect(workHourId | null) — `flags` verilmezse; verilirse
// onSelect(workHourId | null, { [key]: bool }). Çekmeceyi KAPATAN çağırandır (başarıda isOpen=false);
// kayıt başarısız olursa çağıran isOpen'ı açık tutar ve çekmece açık kalır — eski modalla aynı.

// "Vardiyasız Bırak" seçiminin iç işareti (çağırana null gider).
export const OFF = '__off__';

const Subtitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: #7A8694;
  margin-bottom: 12px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ItemBase = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ $selected }) => ($selected ? '#0291EF' : '#E8ECF0')};
  background: ${({ $selected }) => ($selected ? 'rgba(2, 145, 239, 0.06)' : '#fff')};
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  font-family: 'Nunito Sans', sans-serif;
  transition: all 0.12s;

  &:hover {
    border-color: #0291EF;
    background: rgba(2, 145, 239, 0.04);
  }
`;

const ColorChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  background: ${({ $color }) => ($color ? `${$color}26` : '#F5F7FA')};
  color: ${({ $color }) => $color || '#7A8694'};
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
  border: 1px solid ${({ $color }) => ($color ? `${$color}66` : '#E8ECF0')};
`;

const ItemMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #004242;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemTime = styled.span`
  font-size: 12px;
  color: #7A8694;
`;

const ClearItem = styled(ItemBase)`
  color: #C62828;
  border-style: dashed;
  border-color: ${({ $selected }) => ($selected ? '#C62828' : '#E8ECF0')};
  background: ${({ $selected }) => ($selected ? 'rgba(198, 40, 40, 0.06)' : '#fff')};
  &:hover {
    border-color: #C62828;
    background: rgba(198, 40, 40, 0.04);
  }
`;

// Seçimle birlikte gönderilen işaretler (AKM POC §5.1 — fazla mesai vardiyası, hafta tatili).
const FlagBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  background: #FAFBFC;
`;

// Açıklama işaretin ALTINDA, pozitif üst marjla (UI kuralı: negatif marj yok).
const FlagHint = styled.div`
  font-size: 11px;
  color: #7A8694;
  margin-top: 4px;
  padding-left: 30px;
`;

const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: #7A8694;
  font-size: 14px;
`;

const formatTime = (val) => {
  if (!val) return '';
  if (typeof val === 'string' && val.includes(':')) return val.substring(0, 5);
  return String(val);
};

// `flags` (isteğe bağlı): [{ key, label, initial, offOnly?, hint? }] — çekmecede onay kutusu olarak
// görünür ve seçimle birlikte `onSelect(workHourId, { [key]: bool })` ile döner. `offOnly` işaret
// yalnız "Vardiyasız Bırak" seçiminde geçerlidir (ör. rotasyon gününün hafta tatili); bir vardiya
// seçilince false gider. Verilmezse davranış AYNEN eski: onSelect(workHourId).
const flagsSignature = (flags) => flags.map((f) => `${f.key}:${f.initial ? 1 : 0}`).join('|');

// Kaydet'te çağırana giden değerler — saf; testte doğrudan sınanır.
export const resolvePick = (choice, flags, flagValues) => {
  const workHourId = choice === OFF ? null : choice;
  if (flags.length === 0) return [workHourId];
  const resolved = Object.fromEntries(flags.map((f) => [
    f.key,
    f.offOnly && workHourId ? false : !!flagValues[f.key],
  ]));
  return [workHourId, resolved];
};

export default function WorkHourPickerDrawer({
  isOpen,
  onClose,
  onSelect,
  currentWorkHourId,
  subtitle,
  workHours = [],
  flags = [],
  submitLabel,
}) {
  const { t } = useTranslation();
  const [flagValues, setFlagValues] = useState({});
  // null = henüz seçim yok (Kaydet kilitli); OFF = "Vardiyasız Bırak"; aksi halde çalışma saati kimliği.
  const [choice, setChoice] = useState(null);
  const signature = flagsSignature(flags);

  // Her açılışta işaretler hücrenin/günün mevcut değerleriyle, seçim mevcut çalışma saatiyle başlar.
  useEffect(() => {
    if (!isOpen) return;
    setFlagValues(Object.fromEntries(flags.map((f) => [f.key, !!f.initial])));
    setChoice(currentWorkHourId || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, signature, currentWorkHourId]);

  // Çekmeceyi kapatma kararı çağıranındır (başarıda isOpen=false) → GenericFormDrawer'a her zaman
  // false dönülür; aksi halde kayıt başarısız olduğunda da çekmece kapanırdı.
  const handleSave = async () => {
    if (choice === null) return false;
    await onSelect(...resolvePick(choice, flags, flagValues));
    return false;
  };

  const shiftFlags = flags.filter((f) => !f.offOnly);
  const offFlags = flags.filter((f) => f.offOnly);

  const renderFlag = (f) => (
    <div key={f.key}>
      <CheckboxInput
        label={f.label}
        checked={!!flagValues[f.key]}
        onChange={(v) => setFlagValues((prev) => ({ ...prev, [f.key]: !!v }))}
        data-flag={f.key}
      />
      {f.hint && <FlagHint>{f.hint}</FlagHint>}
    </div>
  );

  return (
    <GenericFormDrawer
      open={!!isOpen}
      onClose={onClose}
      title={t('shiftSchedule.pickWorkHour')}
      schema={[]}
      onSave={handleSave}
      submitLabel={submitLabel}
      submitDisabled={choice === null}
    >
      {subtitle && <Subtitle data-testid="work-hour-picker-subtitle">{subtitle}</Subtitle>}

      <List>
        {shiftFlags.length > 0 && <FlagBox>{shiftFlags.map(renderFlag)}</FlagBox>}

        <ClearItem
          type="button"
          $selected={choice === OFF}
          aria-pressed={choice === OFF}
          data-work-hour="off"
          onClick={() => setChoice(OFF)}
        >
          <ColorChip $color="#C62828">
            <Ban size={16} />
          </ColorChip>
          <ItemMeta>
            <ItemName style={{ color: '#C62828' }}>
              {t('shiftSchedule.markAsOff')}
            </ItemName>
            <ItemTime>{t('shiftSchedule.markAsOffHint')}</ItemTime>
          </ItemMeta>
        </ClearItem>

        {offFlags.length > 0 && <FlagBox>{offFlags.map(renderFlag)}</FlagBox>}

        {workHours.length === 0 && (
          <Empty>{t('shiftSchedule.noWorkHours')}</Empty>
        )}

        {workHours.map((wh) => (
          <ItemBase
            key={wh.id}
            type="button"
            $selected={choice === wh.id}
            aria-pressed={choice === wh.id}
            data-work-hour={wh.id}
            onClick={() => setChoice(wh.id)}
          >
            <ColorChip $color={wh.colorCode}>
              {wh.shortCode || ''}
            </ColorChip>
            <ItemMeta>
              <ItemName>{wh.name}</ItemName>
              <ItemTime>
                {formatTime(wh.start)} - {formatTime(wh.end)}
              </ItemTime>
            </ItemMeta>
          </ItemBase>
        ))}
      </List>
    </GenericFormDrawer>
  );
}
